import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  IndexType,
  IntervalType,
  ManualContractCorrectionPayload,
  ManualLineCorrectionPayload,
  SecurityType,
  useGetAgreementImportsBySessionIdContractsQuery,
  usePatchAgreementImportsBySessionIdContractsAndContractIdMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  CurrencyAutocomplete,
  DatepickerV2,
  HelperText,
  Input,
  Label,
  Select,
  Skeleton,
  Typography,
} from '@client/toolkit';

import { useTenantSettingsDefaults } from '../../../../../../../hooks';
import { FieldSourceTag } from './field-source-tag';
import { ReviewLineEditor } from './review-line-editor';
import { extractErrorCodes } from '../utils/extract-error-codes';

const SECURITY_TYPE_OPTIONS: SecurityType[] = ['BankDeposit', 'BankGuarantee', 'Cash', 'Other'];

interface StepReviewProps {
  sessionId: string;
  contractId: string;
  onBack: () => void;
}

/**
 * Falls back to parsing the raw, unparsed source string (shown to the reviewer as
 * "from source file") when the server-interpreted <c>ContractMappedData</c> field is null —
 * e.g. because <c>CompositeFieldParser.TryParseDate</c> didn't recognize the source format.
 * Uses dayjs (more lenient than the server's invariant-culture-only parsing) so the date
 * picker still defaults to something sensible for the reviewer to confirm or correct.
 */
function parseFallbackDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/** Same rationale as {@link parseFallbackDate}, but for plain decimal fields like <c>index_base_value</c>. */
function parseFallbackDecimal(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const parsed = Number(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Same rationale as {@link parseFallbackDate}, but for plain integer fields like <c>notification_deadline_months</c>. */
function parseFallbackInt(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : null;
}

/**
 * Step 4 — field-by-field review for a single contract (Phase 4 of the plan): shows source
 * tags per field, lets the reviewer correct fields (tagged <c>Manual</c> on save), an
 * open-ended-duration (WAULT) warning, and the four Effective-transition rules from
 * <c>IAgreementValidationService</c> as an informational, non-blocking checklist.
 */
export function StepReview({ sessionId, contractId, onBack }: StepReviewProps) {
  const { t: tAgreements } = useTranslation('agreements');
  const { t: tType } = useTranslation('type');
  const { buildingId } = useParams<{ buildingId: string }>();
  const { defaultCurrencyCode } = useTenantSettingsDefaults();

  const { data: contracts, isLoading } = useGetAgreementImportsBySessionIdContractsQuery({ sessionId });
  const contract = useMemo(() => contracts?.find((c) => c.id === contractId), [contracts, contractId]);

  const [contractDraft, setContractDraft] = useState<ManualContractCorrectionPayload>({});
  const [lineDrafts, setLineDrafts] = useState<Record<string, ManualLineCorrectionPayload>>({});

  useEffect(() => {
    if (!contract) return;
    setContractDraft({
      tenantId: contract.mappedData.tenantId,
      durationStart: contract.mappedData.durationStart ?? parseFallbackDate(contract.rawSourceData.durationStartDate),
      durationEnd: contract.mappedData.isOngoing
        ? null
        : contract.mappedData.durationEnd ?? parseFallbackDate(contract.rawSourceData.durationEndDate),
      isOngoing: contract.mappedData.isOngoing,
      indexAdjustmentNote: contract.mappedData.indexAdjustmentNote,
      indexBaseDate: contract.mappedData.indexBaseDate ?? parseFallbackDate(contract.rawSourceData.indexBaseDate),
      indexBaseValue: contract.mappedData.indexBaseValue ?? parseFallbackDecimal(contract.rawSourceData.indexBaseValue),
      indexIntervalCount: contract.mappedData.indexIntervalCount,
      indexIntervalType: contract.mappedData.indexIntervalType,
      indexStartDate: contract.mappedData.indexStartDate ?? parseFallbackDate(contract.rawSourceData.indexStartDate),
      indexAdjustmentType: contract.mappedData.indexAdjustmentType,
      optionLengthCount: contract.mappedData.optionLengthCount,
      optionLengthType: contract.mappedData.optionLengthType,
      notificationDeadlineMonths:
        contract.mappedData.notificationDeadlineMonths ??
        parseFallbackInt(contract.rawSourceData.notificationDeadlineMonths),
      securityAmount: contract.mappedData.securityAmount,
      securityCurrencyCode: contract.mappedData.securityCurrencyCode,
      securityType: contract.mappedData.securityType,
    });
    setLineDrafts(
      Object.fromEntries(
        contract.lines.map((line) => [
          line.id,
          {
            spaceId: line.mappedData.spaceId,
            priceId: line.mappedData.priceId,
            overrideArea: line.mappedData.overrideArea,
            overwrittenUnitAmount: line.mappedData.overwrittenUnitAmount,
          },
        ]),
      ),
    );
  }, [contract]);

  const [correctContract, { isLoading: isSaving, error }] =
    usePatchAgreementImportsBySessionIdContractsAndContractIdMutation();

  const errorCodes = extractErrorCodes(error);

  const handleSave = async () => {
    try {
      await correctContract({
        sessionId,
        contractId,
        body: { contract: contractDraft, lines: lineDrafts },
      }).unwrap();
    } catch {
      /* error is surfaced below via the errorCodes list */
    }
  };

  if (isLoading || !contract) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">
          {tAgreements('agreementImport.review.title', { documentId: contract.documentId })}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {contract.hasOpenEndedDuration && (
          <div className="rounded-md border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            {tAgreements('agreementImport.review.openEndedWarning')}
          </div>
        )}

        <div className="space-y-3">
          <div className="font-medium text-sm text-white/80">
            {tAgreements('agreementImport.review.contractFields')}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2 col-span-2">
              <Label>{tAgreements('agreementImport.review.tenant')}</Label>
              <div className="flex items-center gap-2">
                <Typography variant="primary">{contract.rawSourceData.tenantName ?? '—'}</Typography>
                <FieldSourceTag source={contract.fieldSources.tenantId} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.durationStart')}</Label>
              <div className="flex items-center gap-2">
                <DatepickerV2
                  className="w-full"
                  value={dayjs(contractDraft.durationStart)}
                  onChange={(date) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      durationStart: date?.isValid() ? date.format('YYYY-MM-DD') : null,
                    }))
                  }
                />
                <FieldSourceTag source={contract.fieldSources.durationStart} />
              </div>
            </div>

            {!contractDraft.isOngoing && (
              <div>
                <Label>{tAgreements('agreementImport.review.durationEnd')}</Label>
                <div className="flex items-center gap-2">
                  <DatepickerV2
                    className="w-full"
                    value={dayjs(contractDraft.durationEnd)}
                    onChange={(date) =>
                      setContractDraft((prev) => ({
                        ...prev,
                        durationEnd: date?.isValid() ? date.format('YYYY-MM-DD') : null,
                      }))
                    }
                  />
                  <FieldSourceTag source={contract.fieldSources.durationEnd} />
                </div>
              </div>
            )}

            <div className="col-span-2 flex items-center gap-2">
              <Checkbox
                checked={!!contractDraft.isOngoing}
                onChange={(e) =>
                  setContractDraft((prev) => ({
                    ...prev,
                    isOngoing: e.target.checked,
                    durationEnd: e.target.checked ? null : prev.durationEnd,
                  }))
                }
              />
              <Label className="!mb-0">{tAgreements('agreementImport.review.isOngoing')}</Label>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.indexAdjustmentNote')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  size="medium"
                  value={contractDraft.indexAdjustmentNote ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({ ...prev, indexAdjustmentNote: e.target.value || null }))
                  }
                />
                <FieldSourceTag source={contract.fieldSources.indexAdjustmentNote} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.indexAdjustmentType')}</Label>
              <div className="flex items-center gap-2">
                <Select
                  size="medium"
                  value={contractDraft.indexAdjustmentType ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      indexAdjustmentType: (e.target.value || null) as IndexType | null,
                    }))
                  }
                >
                  <option value="" />
                  <option value="Custom">{tAgreements('agreementImport.review.indexAdjustmentTypeCustom')}</option>
                  <option value="NorskKPI">{tAgreements('agreementImport.review.indexAdjustmentTypeNorskKPI')}</option>
                </Select>
                <FieldSourceTag source={contract.fieldSources.indexAdjustmentType} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.indexBaseDate')}</Label>
              <div className="flex items-center gap-2">
                <DatepickerV2
                  className="w-full"
                  value={contractDraft.indexBaseDate ? dayjs(contractDraft.indexBaseDate) : null}
                  views={['month', 'year']}
                  onChange={(date) => {
                    setContractDraft((prev) => ({
                      ...prev,
                      indexBaseDate: date?.isValid() ? date.format('YYYY-MM-DD') : null,
                    }));
                  }}
                />
                <FieldSourceTag source={contract.fieldSources.indexBaseDate} />
              </div>
            </div>

            {contractDraft.indexAdjustmentType === 'Custom' && (
              <div>
                <Label>{tAgreements('agreementImport.review.indexBaseValue')}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    size="medium"
                    value={contractDraft.indexBaseValue ?? ''}
                    onChange={(e) =>
                      setContractDraft((prev) => ({
                        ...prev,
                        indexBaseValue: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                  />
                  <FieldSourceTag source={contract.fieldSources.indexBaseValue} />
                </div>
              </div>
            )}

            <div>
              <Label>{tAgreements('agreementImport.review.indexStartDate')}</Label>
              <div className="flex items-center gap-2">
                <DatepickerV2
                  className="w-full"
                  value={contractDraft.indexStartDate ? dayjs(contractDraft.indexStartDate) : null}
                  onChange={(date) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      indexStartDate: date?.isValid() ? date.format('YYYY-MM-DD') : null,
                    }))
                  }
                />
                <FieldSourceTag source={contract.fieldSources.indexStartDate} />
              </div>
            </div>

            <div className="col-span-2">
              <Label>{tAgreements('agreementImport.review.indexInterval')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  size="medium"
                  className="w-24"
                  placeholder={tAgreements('agreementImport.review.indexIntervalCount')}
                  value={contractDraft.indexIntervalCount ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      indexIntervalCount: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <Select
                  size="medium"
                  value={contractDraft.indexIntervalType ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      indexIntervalType: (e.target.value || null) as IntervalType | null,
                    }))
                  }
                >
                  <option value="" />
                  <option value="Month">
                    {tType('IntervalType.Month', { count: contractDraft.indexIntervalCount ?? 1 })}
                  </option>
                  <option value="Year">
                    {tType('IntervalType.Year', { count: contractDraft.indexIntervalCount ?? 1 })}
                  </option>
                </Select>
                <FieldSourceTag source={contract.fieldSources.indexIntervalCount} />
              </div>
            </div>

            <div className="col-span-2">
              <Label>{tAgreements('agreementImport.review.optionLength')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  size="medium"
                  className="w-24"
                  placeholder={tAgreements('agreementImport.review.optionLengthCount')}
                  value={contractDraft.optionLengthCount ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      optionLengthCount: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <Select
                  size="medium"
                  value={contractDraft.optionLengthType ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      optionLengthType: (e.target.value || null) as IntervalType | null,
                    }))
                  }
                >
                  <option value="" />
                  <option value="Month">
                    {tType('IntervalType.Month', { count: contractDraft.optionLengthCount ?? 1 })}
                  </option>
                  <option value="Year">
                    {tType('IntervalType.Year', { count: contractDraft.optionLengthCount ?? 1 })}
                  </option>
                </Select>
                <FieldSourceTag source={contract.fieldSources.optionLengthCount} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.notificationDeadlineMonths')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  size="medium"
                  value={contractDraft.notificationDeadlineMonths ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      notificationDeadlineMonths: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <FieldSourceTag source={contract.fieldSources.notificationDeadlineMonths} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.securityType')}</Label>
              <div className="flex items-center gap-2">
                <Select
                  size="medium"
                  value={contractDraft.securityType ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      securityType: (e.target.value || null) as SecurityType | null,
                    }))
                  }
                >
                  <option value="" />
                  {SECURITY_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>
                      {tType(`SecurityType.${type}`)}
                    </option>
                  ))}
                </Select>
                <FieldSourceTag source={contract.fieldSources.securityType} />
              </div>
            </div>

            <div>
              <Label>{tAgreements('agreementImport.review.securityAmount')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  size="medium"
                  className="w-32"
                  value={contractDraft.securityAmount ?? ''}
                  onChange={(e) =>
                    setContractDraft((prev) => ({
                      ...prev,
                      securityAmount: e.target.value === '' ? null : Number(e.target.value),
                    }))
                  }
                />
                <CurrencyAutocomplete
                  size="medium"
                  selected={contractDraft.securityCurrencyCode ?? ''}
                  defaultCurrencyCode={defaultCurrencyCode}
                  onChange={(code) => setContractDraft((prev) => ({ ...prev, securityCurrencyCode: code || null }))}
                />
                <FieldSourceTag source={contract.fieldSources.securityAmount} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {contract.lines.map((line, index) => (
            <ReviewLineEditor
              key={line.id}
              line={line}
              index={index}
              buildingId={buildingId ?? ''}
              draft={lineDrafts[line.id] ?? {}}
              onChange={(draft) => setLineDrafts((prev) => ({ ...prev, [line.id]: draft }))}
            />
          ))}
        </div>

        <div className="space-y-2">
          <div className="font-medium text-sm text-white/80">
            {tAgreements('agreementImport.review.fourRulesTitle')}
          </div>
          <ul className="space-y-1 text-sm">
            {contract.readiness.rules.map((rule) => (
              <li key={rule.rule} className={rule.isSatisfied ? 'text-teal-400' : 'text-red-400'}>
                {rule.isSatisfied ? '✓' : '✗'}{' '}
                {tAgreements(`agreement.validationErrors.${rule.rule}`, { defaultValue: rule.rule })}
              </li>
            ))}
          </ul>
        </div>

        {errorCodes.length > 0 && (
          <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4">
            <ul className="list-disc pl-5 text-sm text-red-200 space-y-1">
              {errorCodes.map((code) => (
                <li key={code}>{tAgreements(`agreementImport.validationErrors.${code}`, { defaultValue: code })}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-between">
          <Button color="primary-dark" onClick={onBack}>
            {tAgreements('agreementImport.review.backButton')}
          </Button>
          <Button color="primary" progressing={isSaving} onClick={handleSave}>
            {tAgreements('agreementImport.review.saveButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
